import * as R from 'ramda';
import { useMemo } from 'react';
// features
import { setExpandedContainerOptions } from '../../expanded-container/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import routesMap from '../../../utilities/routes';
//////////////////////////////////////////////////

const tableSettings = {
  maxHeight: 320,
  cellFontSize: 12,
  titleFontSize: 12,
  titleRowHeight: 50,
  allowEditBtn: true,
  tableRowHeight: 32,
  useMainColors: true,
  allowSelectAll: false,
  checkBoxCellWidth: 54,
  allowSelectItems: false,
  emptyListPadding: '10px 0',
  tableWrapperProps: { m: '2px' },
  checkBoxCellJustifyContent: 'flex-start',
};

const makeReport = ({ report, itemList, columnSettings, hideEmptyFields }) => {
  let reportToUse = report;

  if (R.isNil(report)) {
    reportToUse = {
      fields: G.mapIndexed((name, sequence) => ({ name, sequence }), R.keys(columnSettings)),
    };
  }

  if (R.not(hideEmptyFields)) return reportToUse;

  const fields = R.filter(
    ({ name }) => G.isNotNilAndNotEmpty(R.path([0, ...R.split('.', name)], itemList)),
    reportToUse.fields,
  );

  return { fields };
};

const systemFields = {
  [GC.FIELD_CREATED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:created-date',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:updated-date',
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 150,
    name: 'titles:updated-by',
  },
};

export const useTableProps = ({
  fields,
  report,
  memoDeps,
  itemList,
  dispatch,
  endpoints,
  hideEmptyFields,
  downloadOptions,
  omitColumns = [],
  handleRemoveItem,
  expandedContainer,
  getItemListRequest,
  getEditPermissions,
  primaryObjectGuidKey,
  getRemovePermissions,
  handlePreviewDocument,
  handleDownloadDocument,
  additionalTableSettings,
  handleCreateOrUpdateItem,
  actionsPicker = ['edit', 'remove'],
}) => {
  const tableProps = useMemo(() => {
    const handleGoToContractDetails = ({ guid, type = 'carrier' }) => {
      if (expandedContainer) {
        const map = {
          carrier: GC.PAGE_CARRIER_CONTRACT_DETAILS,
          customer: GC.PAGE_CUSTOMER_CONTRACT_DETAILS,
        };

        return dispatch(setExpandedContainerOptions({
          opened: true,
          visitPageGuid: guid,
          componentType: R.prop(type, map),
        }));
      }

      const map = {
        carrier: routesMap.carrierContractDetails(guid),
        customer: routesMap.customerContractDetails(guid),
      };

      G.goToRoute(R.prop(type, map));
    };

    const actionButtonsMap = {
      edit: {
        iconName: 'pencil',
        getPermissions: getEditPermissions,
        action: (guid, entity) => handleCreateOrUpdateItem(entity),
      },
      remove: {
        iconName: 'trash',
        action: handleRemoveItem,
        getPermissions: getRemovePermissions,
      },
      terminalProcess: {
        iconSize: [15, 15],
        iconName: 'moneyWithArrow',
        action: (guid, entity) => handleCreateOrUpdateItem({ ...entity, activeTab: 1 }),
      },
      terminalFinance: {
        iconSize: [15, 15],
        iconName: 'payment',
        action: (guid, entity) => handleCreateOrUpdateItem({ ...entity, activeTab: 2 }),
      },
      goToUserProfile: {
        iconName: 'pencil',
        action: (guid) => G.goToRoute(routesMap.getUserSettingsRoute(guid)),
      },
      goToCarrierContractDetails: {
        iconName: 'pencil',
        action: (guid) => handleGoToContractDetails({ guid }),
      },
      goToCustomerContractDetails: {
        iconName: 'pencil',
        action: (guid) => handleGoToContractDetails({ guid, type: 'customer' }),
      },
      downloadDocument: {
        iconName: 'downloadDocument',
        getPermissions: ({ fileUri }) => G.ifElse(R.isNil(fileUri), ''),
        action: (guid, entity) => handleDownloadDocument(
          entity,
          downloadOptions,
          { primaryObjectGuidKey, endpointName: G.getPropFromObject('downloadDocument', endpoints) },
        ),
      },
      previewDocument: {
        iconName: 'eye',
        getPermissions: ({ fileUri }) => G.ifElse(R.isNil(fileUri), ''),
        action: (guid, entity) => handlePreviewDocument(
          entity,
          downloadOptions,
          { primaryObjectGuidKey, endpointName: G.getPropFromObject('downloadDocument', endpoints) },
        ),
      },
      previewSharedAccessorial: {
        iconName: 'eye',
        getPermissions: ({ shared }) => G.ifElse(R.not(shared), ''),
        action: (guid, entity) => handleCreateOrUpdateItem(entity),
      },
      refreshList: {
        iconName: 'refresh',
        action: () => getItemListRequest(),
      },
    };

    const actionButtons = R.values(R.pick(actionsPicker, actionButtonsMap));

    const columnSettings = R.compose(
      R.omit(omitColumns),
      R.mergeLeft(systemFields),
      R.map(({ uiType, ...item }) => ({
        ...R.pick(['width', 'sortable', 'searchable', GC.FIELD_NAME, 'customComponent'], item),
        [GC.FIELD_TYPE]: uiType,
      })),
    )(fields);

    return {
      actionButtons,
      columnSettings,
      tableSettings: R.mergeRight(tableSettings, additionalTableSettings),
      report: makeReport({ report, itemList, columnSettings, hideEmptyFields }),
    };
  }, memoDeps);

  return tableProps;
};
